// @ts-nocheck
import { useState, useEffect, useRef, useCallback, useId } from 'react';
import dynamic from 'next/dynamic';
import { CarouselProps, ControlProps, ScrollMode } from 'nuka-carousel';
import beTwMerge from '@/utils/beTwMerge';
import Slider from '@/components/Slider/Slider';
import { IItem, IVideoItem } from './types';
import LeftNavArrow from './LeftNavArrow';
import RightNavArrow from './RightNavArrow';

const CarouselItem = dynamic(() => import('./CarouselItem'));
const CarouselItemVideo = dynamic(() => import('./CarouselItemVideo'));

export interface ICarouselInfo {
  name: string;
  type: 'product' | 'category' | 'video' | 'mission';
}

export interface ICarouselCustomStyles {
  container: string;
  image: string;
  arrowPositions: string;
}

interface ICarouselProps {
  items: IItem[] | IVideoItem[] | IItemWithGAEvents[];
  isProduct?: boolean;
  info: ICarouselInfo;
  customItemStyles?: ICarouselCustomStyles;
  slidesToShow?: number;
}

const carouselTypeOptions = {
  slidesToShow: {
    product: 5.5,
    category: 3.5,
    video: 5.5,
    mission: 4,
  },
  minimumItems: {
    product: 4,
    category: 3,
    video: 6,
    mission: 4,
  },
  minimumItemsMessage: {
    product: 'Carousel must have at least 4 items',
    category: 'Carousel must have at least 3 items',
    video: 'Carousel must have at least 6 items',
    mission: 'Carousel must have 4 items',
  },
};

export default function Carousel({ info, isProduct, items, customItemStyles, slidesToShow }: ICarouselProps) {
  const [isGrid, setIsGrid] = useState(false);
  const { type } = info;

  const containerRef = useRef(null);
  const [visibleIndexes, setVisibleIndexes] = useState([]);

  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const numberOfSlides = items.length;

  const randomId = useId();

  const setSlidesToShow = useCallback(() => {
    if (slidesToShow) return slidesToShow;
    if (type === 'mission') {
      return 2.5;
    }
    if (isMobile) {
      return 1.5;
    }
    if (isTablet) {
      if (type === 'product') {
        return 3.5;
      }
      return 2.5;
    }
    return carouselTypeOptions.slidesToShow[type];
  }, [type, isMobile, isTablet, slidesToShow]);

  function setSlidesToScroll() {
    if (type === 'mission') {
      return 2;
    }
    if (isMobile) {
      return 1;
    }
    if (isTablet) {
      return 2;
    }
    return Math.floor(carouselTypeOptions.slidesToShow[type]);
  }

  function setIndexesAfterChange() {
    const childrenArray = Array.from(containerRef.current.querySelectorAll('.slide-visible'));
    const indexesArray = childrenArray.map(child =>
      parseInt(child.querySelector('.slide-outer').getAttribute('data-index'), 10)
    );

    const lastIndex = indexesArray[indexesArray.length - 1];
    const newIndexesArray = [...indexesArray];
    if (lastIndex <= numberOfSlides) {
      newIndexesArray.push(lastIndex + 1);
    }
    setVisibleIndexes(newIndexesArray);

    const slides = containerRef.current.querySelectorAll('.slide');
    slides.forEach(slide => {
      slide.classList.add('slide-visible');
      slide.removeAttribute('inert');
    });
  }

  function setbeforeSlide() {
    const slides = containerRef.current.querySelectorAll('.slide');
    slides.forEach(slide => {
      slide.classList.add('slide-visible');
      slide.removeAttribute('inert');
    });
  }

  const isUseNewStyles = isProduct && (slidesToShow === 4 || slidesToShow === 6);

  const carouselConfig: CarouselProps = {
    carouselId: randomId.toLowerCase().replaceAll(':', ''),
    cellAlign: 'left',
    cellSpacing: 0,
    className: type === 'mission' ? 'be-nuka-slider is-mission-carousel' : 'be-nuka-slider',
    disableEdgeSwiping: true,
    dragThreshold: 0.2,
    enableKeyboardControls: true,
    slidesToShow: setSlidesToShow(),
    slidesToScroll: setSlidesToScroll(),
    scrollMode: ScrollMode.remainder,
    wrapAround: false,
    renderBottomCenterControls: () => null,
    renderCenterLeftControls: (props: ControlProps) => (
      <LeftNavArrow
        btnClass={isUseNewStyles ? 'tw-w-9 tw-h-9' : ''}
        containerClass={isUseNewStyles ? 'tw-left-2' : ''}
        item={items[0]}
        {...props}
      />
    ),
    renderCenterRightControls: (props: ControlProps) => (
      <RightNavArrow
        btnClass={isUseNewStyles ? 'tw-w-9 tw-h-9' : ''}
        containerClass={isUseNewStyles ? 'tw-right-2' : ''}
        item={items[0]}
        isUseNewStyles={isProduct && (slidesToShow === 4 || slidesToShow === 6)}
        {...props}
      />
    ),
    afterSlide: () => setIndexesAfterChange(),
    beforeSlide: () => setbeforeSlide(),
  };

  function setState() {
    if (numberOfSlides < carouselTypeOptions.minimumItems[type]) {
      return 'error';
    }
    if (numberOfSlides < 5 && !isMobile) {
      return 'grid';
    }
    return 'carousel';
  }

  const state = setState();

  useEffect(() => {
    if (items.length <= 4) {
      setIsGrid(true);
    } else {
      setIsGrid(false);
    }
  }, [items]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const tabletQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');

    setIsMobile(mobileQuery.matches);
    setIsTablet(tabletQuery.matches);

    const updateMediaQueries = e => {
      if (e.media === mobileQuery.media) {
        setIsMobile(e.matches);
      } else if (e.media === tabletQuery.media) {
        setIsTablet(e.matches);
      }
    };

    mobileQuery.addListener(updateMediaQueries);
    tabletQuery.addListener(updateMediaQueries);

    return () => {
      mobileQuery.removeListener(updateMediaQueries);
      tabletQuery.removeListener(updateMediaQueries);
    };
  }, []);

  useEffect(() => {
    const initialIndexes = Array.from({ length: setSlidesToShow() + 1 }, (_, index) => index);
    setVisibleIndexes(initialIndexes);
  }, [isMobile, isTablet, setSlidesToShow]);

  return (
    <div
      ref={containerRef}
      className={beTwMerge(
        'slides-wrapper tw-w-[calc(100vw-32px)] lg:tw-w-full tw-relative tw-m-auto tw-overflow-hidden tw-min-h-[200px]',
        items.length <= 4 ? 'tw-max-w-[1440px] lg:tw-w-auto min-[1504px]:tw-m-auto' : '',
        isProduct && 'tw-w-[calc(100vw-16px)] tw-m-0'
      )}
    >
      {
        {
          grid: (
            <div className="regular slider is-full-bleed">
              {items.map((item, idx) => (
                <div key={`${info?.name}-item-${idx}`} className="slide-outer tw-w-full">
                  {type === 'video' ? (
                    <CarouselItemVideo
                      index={idx}
                      item={item}
                      isActive
                      isGrid={isGrid}
                      customItemStyles={customItemStyles}
                      onLoad={() => setIsLoaded(true)}
                    />
                  ) : (
                    <CarouselItem
                      index={idx}
                      isActive
                      item={item}
                      isGrid={isGrid}
                      isProduct={isProduct}
                      customItemStyles={customItemStyles}
                      onLoad={() => setIsLoaded(true)}
                    />
                  )}
                </div>
              ))}
            </div>
          ),
          carousel: (
            <Slider type={type} isMobile={isMobile} {...carouselConfig}>
              {items.map((item, idx) => (
                <div key={`${info?.name}-item-${idx}`} className="slide-outer tw-w-full" data-index={idx}>
                  {type === 'video' ? (
                    <CarouselItemVideo
                      index={idx}
                      item={item}
                      isActive={visibleIndexes.includes(idx)}
                      isGrid={isGrid}
                      customItemStyles={customItemStyles}
                      onLoad={() => setIsLoaded(true)}
                    />
                  ) : (
                    <CarouselItem
                      index={idx}
                      isActive={visibleIndexes.includes(idx)}
                      item={item}
                      isProduct={isProduct}
                      isGrid={isGrid}
                      customItemStyles={customItemStyles}
                      onLoad={() => setIsLoaded(true)}
                    />
                  )}
                </div>
              ))}
            </Slider>
          ),
          error: <h4 className="tw-text-color-dark-rose tw-italic">{carouselTypeOptions.minimumItemsMessage[type]}</h4>,
        }[state]
      }
    </div>
  );
}
